import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

const KIND_OPTIONS = ["", "FDA_DECISION", "DRUG_APPROVAL", "DRUG_REJECTION", "CLINICAL_TRIAL", "MEDICAL_DEVICE"];
function sevColor(sev) { return { LOW: "#7AC860", MEDIUM: "#F5C842", HIGH: "#E8A020", EXTREME: "#C84A3A" }[sev] || "#60B8E8"; }

export default function AdminHealthcareMap() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({ kind: "", service: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.kind) params.kind = filter.kind;
      if (filter.service) params.service = filter.service;
      const [evRes, sumRes] = await Promise.all([
        adminApi.healthcareMap(params),
        adminApi.healthcareMapSummary(),
      ]);
      setEvents(evRes.data?.events || []);
      setSummary(sumRes.data || null);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const services = Object.keys(summary?.services || {}).sort();

  return (
    <div className="admin-page mesh-shell" data-testid="admin-healthcare-map">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />
      {/* Phase 26C — Mandated Compliance Lock™ banner (Healthcare public-only) */}
      <ComplianceLock variant="banner" testid="admin-healthcare-compliance" />

      <div className="admin-page__head">
        <h2 className="admin-page__title">Healthcare Intelligence Map</h2>
        <div className="mesh-eyebrow">FDA · Drugs · Trials · Devices → CareLogix services</div>
      </div>

      {summary && (
        <section className="admin-section mesh-section" data-testid="hmap-summary">
          <h3 className="admin-section__title">Coverage Summary</h3>
          <div className="mesh-exec-grid">
            <div className="mesh-exec-cell"><div className="mesh-exec-cell__num">{summary.total}</div><div className="mesh-exec-cell__lbl">Events</div></div>
            {Object.entries(summary.kinds || {}).map(([k, v]) => (
              <div key={k} className="mesh-exec-cell"><div className="mesh-exec-cell__num">{v}</div><div className="mesh-exec-cell__lbl">{k.replace(/_/g, " ")}</div></div>
            ))}
          </div>
        </section>
      )}

      <section className="admin-section mesh-section">
        <div className="admin-filters" style={{ marginBottom: 10 }}>
          <select value={filter.kind} onChange={(e) => setFilter((f) => ({ ...f, kind: e.target.value }))} data-testid="hmap-filter-kind">
            {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k || "All Kinds"}</option>)}
          </select>
          <select value={filter.service} onChange={(e) => setFilter((f) => ({ ...f, service: e.target.value }))} data-testid="hmap-filter-svc">
            <option value="">All Services</option>
            {services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" className="admin-linkbtn" onClick={load} data-testid="hmap-refresh">Refresh</button>
        </div>

        {loading ? <div className="admin-saved">Loading…</div> : (
          <table className="admin-table" data-testid="hmap-table">
            <thead>
              <tr>
                <th>Kind</th><th>Asset</th><th>Severity</th><th>Title</th><th>Services impacted</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td>{ev.kind}</td>
                  <td>{ev.asset || "—"}</td>
                  <td><span className="mesh-pill" style={{ borderColor: sevColor(ev.severity), color: sevColor(ev.severity) }}>{ev.severity}</span></td>
                  <td>{ev.title}</td>
                  <td>{(ev.services || []).map((s) => <span key={s} className="mesh-pill mesh-pill--ghost" style={{ marginRight: 4 }}>{s}</span>)}</td>
                  <td>{ev.created_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={6} className="admin-foot-note">No events match filters.</td></tr>}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
